import * as yup from "yup";

import { TODO_TASKS_STATUS_AVAILABLE, cn } from "../../../lib/utils";
import { useEffect, useState } from "react";

import ModalContainer from "../ModalContainer";
import MultiSelect from "../../multiselect/MultiSelect";
import SingleSelect from "../../singleselect/SingleSelect";
import Tabs from "../../tabs/Tabs";
import TextareaInput from "../../input/TextareaInput";
import { createTask } from "../../../services/tasks.service";
import { getAllTaskUsers } from "../../../helper/data.helper";
import { getTaskCategories } from "../../../services/categories.service";
import { useForm } from "react-hook-form";
import useNotification from "../../../hooks/notification.hook";
import { useSessionStore } from "../../../store/session.store";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

const TASK_STATUS_LIST = Object.values(TODO_TASKS_STATUS_AVAILABLE).map((status) => ({
	label: status.label,
	value: status.value,
}));

export default function CreateTaskModal({
	open,
	setOpen,
	organizationId,
	bodyParams,
	onInsert = () => {},
}) {
	const { t } = useTranslation();
	const [isSuccess, setIsSuccess] = useState(false);
	const [users, setUsers] = useState([]);
	const [task, setTask] = useState(null);
	const [isUserLoading, setIsUserLoading] = useState();
	const [categories, setCategories] = useState([]);
	const [isCategoryLoading, setIsCategoryLoading] = useState(false);

	const { user } = useSessionStore();

	// Con una sola categoria non c'è nulla da scegliere: il select resta nascosto
	const canManageCategories = categories.length > 1;

	// Set message
	yup.setLocale({
		// use constant translation keys for messages without values
		mixed: {
			required: ({ path }) => t("common:errors.required", { field: t(`common:fields.${path}`) }),
		},
		string: {
			max: ({ max, path }) =>
				t("common:errors.max_length", {
					field: t(`common:fields.${path}`),
					value: max,
				}),
		},
		number: {
			moreThan: ({ path, more }) =>
				t("common:errors.more_than", {
					field: t(`common:fields.${path}`),
					value: more,
				}),
			lessThan: ({ path, less }) =>
				t("common:errors.less_than", {
					field: t(`common:fields.${path}`),
					value: less,
				}),
		},
	});

	// Schema Yup
	const schema = yup.object().shape({
		title: yup.string().required().max(255),
		description: yup.string(),
		priority: yup.string(),
		due_date: yup.string().required(),
		assigned_to_ids: yup.array().of(yup.number()),
		status: yup.string(),
		task_category_id: canManageCategories ? yup.string().required() : yup.string(),
	});

	const {
		register,
		reset,
		handleSubmit,
		formState: { errors },
		watch,
		setValue,
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			status: TASK_STATUS_LIST[0].value,
			assigned_to_ids: user?.id ? [user.id] : [],
		},
	});

	const { openNotification, helperErrorNotification } = useNotification();

	const [loading, setLoading] = useState(false);

	const toUserItem = (u) => ({
		id: u.id,
		label: `${u.first_name} ${u.last_name}`,
	});

	const userListItems = users.map(toUserItem);

	const categoryListItems = categories.map((category) => ({
		id: category.id,
		label: category.label,
	}));

	const selectedCategory = categoryListItems.find(
		(item) => String(item.id) === String(watch("task_category_id"))
	);

	// Gli utenti assegnabili dipendono dalla categoria: senza categoria non si sceglie
	const isAssignDisabled = !organizationId || isCategoryLoading || !selectedCategory;

	const isOrganizationUser = (u) => u.role === "reseller" || u.role === "organization";

	const userGroupsListItems = [
		{
			label: "Transizione Digitale",
			listItems: users.filter((u) => !isOrganizationUser(u)).map(toUserItem),
		},
		{
			label: "Ente",
			listItems: users.filter(isOrganizationUser).map(toUserItem),
		},
	].filter((group) => group.listItems.length > 0);

	const convertToTipTapJSON = (text) => {
		const paragraphs = text.split("\n");

		const content = {
			type: "doc",
			content: paragraphs.map((paragraph, i) => {
				if (paragraph.trim()) {
					return {
						attrs: { textAlign: null },
						type: "paragraph",
						content: [
							{
								type: "text",
								text: paragraph.trim(),
							},
						],
					};
				} else {
					return {
						attrs: { textAlign: null },
						type: "paragraph",
					};
				}
			}),
		};

		return content;
	};

	/**
	 * Form Handler
	 * @param {form modal} data
	 */
	async function formHandler(data) {
		if (!loading && organizationId) {
			setLoading(true);

			const description = convertToTipTapJSON(data.description);

			const body = {
				organizationId: organizationId,
				title: data.title,
				due_date: data.due_date,
				description: data.description ? description : undefined,
				assigned_to_ids: data.assigned_to_ids,
				status: data.status,
				priority: data.priority,
				task_category_id: data.task_category_id ? Number(data.task_category_id) : undefined,
				...bodyParams,
			};

			// Create plan
			createTask(
				body,
				(res) => {
					setLoading(false);
					openNotification({
						text: "Attività creata con successo!",
						status: "success",
					});
					onInsert(res);
					setTask(res.data);
					setIsSuccess(true);
				},
				(error) => {
					helperErrorNotification(error);
					setLoading(false);
				}
			);
		}
	}

	const getUsersList = async (taskCategoryId) => {
		try {
			setIsUserLoading(true);
			const users = await getAllTaskUsers({
				organizationId,
				filter: { task_category_id: taskCategoryId },
			});
			setUsers(users);
			setIsUserLoading(false);
			// L'utente corrente si preseleziona solo se assegnabile su questa categoria
			requestAnimationFrame(() =>
				setValue("assigned_to_ids", users.some((u) => u.id === user?.id) ? [user.id] : [])
			);
		} catch {
			setIsUserLoading(false);
		}
	};

	const onSelectCategory = (taskCategoryId) => {
		// La lista degli assegnabili cambia con la categoria: si riparte da zero
		setValue("task_category_id", taskCategoryId ? String(taskCategoryId) : "");
		setValue("assigned_to_ids", []);
		setUsers([]);

		if (taskCategoryId) getUsersList(taskCategoryId);
	};

	const getCategoriesList = () => {
		setIsCategoryLoading(true);

		getTaskCategories(
			{ organizationId },
			(res) => {
				setCategories(res.data);
				setIsCategoryLoading(false);

				// Una sola categoria: nessuna scelta da fare, si procede con quella
				if (res.data.length === 1) onSelectCategory(res.data[0].id);
			},
			(error) => {
				helperErrorNotification(error);
				setIsCategoryLoading(false);
			}
		);
	};

	/**
	 * Errors Form
	 */
	useEffect(() => {
		if (Object.keys(errors).length) {
			const fields = Object.values(errors).map((er) => er.message);
			openNotification({ fields, status: "error" });
		}
	}, [errors]);

	/**
	 * Init value
	 */
	useEffect(() => {
		if (open) {
			reset();
			setIsSuccess(false);
			setUsers([]);
			setCategories([]);
			getCategoriesList();
		}
	}, [open]);

	return (
		<ModalContainer isOpen={open} setIsOpen={setOpen}>
			<div className="main_modal_content">
				<button
					className="main_close"
					onClick={() => setOpen(false)}
					type="button"
					aria-label="Chiudi"
				>
					<span className="icon_10 default close icon_mask !bg-white"></span>
				</button>

				{!isSuccess ? (
					<>
						<div className="main_header_modal left_text">
							<div className="flex items-center gap-[12px]">
								<div className="icon_24 default todotd flex-none"></div>
								<span className="title">Creazione attività in TodoTD</span>
							</div>
							<div className="text-[12px] pt-[12px] text-dark-50">
								Compila i campi sottostanti per generare una nuova attività attribuita all’ente ed
								al piano su cui stai operando
							</div>
						</div>

						{/* Modal Form */}
						<div className={"main_body_modal"}>
							{canManageCategories && !selectedCategory && (
								<div className="mb-5">
									<div className="main_message_box box_info">
										<div className="main_icon">
											<span className="icon_16 default info"></span>
										</div>
										<div className="message">
											Selezionare una categoria per abilitare la scelta degli assegnatari
										</div>
									</div>
								</div>
							)}

							<form
								id="crud-task-form"
								className="global_form_landing"
								onSubmit={handleSubmit(formHandler)}
								noValidate
							>
								{/* Priority */}
								<div className="base">
									<div className="label_input">Priorità</div>
									<Tabs
										size="small"
										theme="light"
										selected={watch("priority")}
										onChange={(value) =>
											setValue("priority", watch("priority") !== value ? value : undefined)
										}
										options={[
											{
												label: (
													<div className="flex items-center gap-2 justify-center">
														<span className="icon_16 default priority_low" />
														Bassa
													</div>
												),
												value: "low",
											},
											{
												label: (
													<div className="flex items-center gap-2 justify-center">
														<span className="icon_16 default priority_medium" />
														Media
													</div>
												),
												value: "medium",
											},
											{
												label: (
													<div className="flex items-center gap-2 justify-center">
														<span className="icon_16 default priority_high" />
														Alta
													</div>
												),
												value: "high",
											},
										]}
									/>
								</div>

								{/* Stato */}
								<div className="base">
									<div className="main_select_landing">
										<div className="relative">
											<label htmlFor="crud-task-status" className="label_input">
												Stato
											</label>
											<div className="relative">
												<select
													{...register("status")}
													id="crud-task-status"
													className={cn("landing", errors.status && "err")}
												>
													{TASK_STATUS_LIST.map((status) => (
														<option value={status.value} key={status.value}>
															{status.label}
														</option>
													))}
												</select>
												<div className="wrap_arrow">
													<span
														className={cn("icon_16 default chevron icon_mask !bg-interactive-100")}
													></span>
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Title */}
								<div className="base">
									<div className="wrap_input_landing">
										<label htmlFor="crud-task-title" className="label_input">
											Titolo
										</label>
										<TextareaInput
											className={`!min-h-[32px] ${errors.title ? " err" : ""}`}
											id="crud-task-title"
											{...register("title")}
										/>
									</div>
								</div>

								{/* Description */}
								<div className="base">
									<div className="wrap_input_landing">
										<label htmlFor="crud-task-description" className="label_input">
											Descrizione
										</label>
										<TextareaInput
											className={errors.description ? " err" : ""}
											id="crud-task-description"
											{...register("description")}
										/>
									</div>
								</div>

								{/* Category */}
								{(isCategoryLoading || canManageCategories) && (
									<div className="base">
										<div className="label_input">Categoria</div>
										<SingleSelect
											listItems={categoryListItems}
											selectedItem={selectedCategory}
											onSelect={(item) => onSelectCategory(item?.id)}
											placeholder="Seleziona una categoria"
											isLoading={isCategoryLoading}
											disabled={!isCategoryLoading && !categories.length}
											error={!!errors.task_category_id}
										/>
									</div>
								)}

								{/* User */}
								<div className="base">
									<label htmlFor="crud-task-assigned" className="label_input">
										Assegnato a
									</label>
									<MultiSelect
										groupsListItems={userGroupsListItems}
										selectedItems={userListItems.filter((item) =>
											(watch("assigned_to_ids") ?? []).includes(item.id)
										)}
										onSelect={(items) =>
											setValue(
												"assigned_to_ids",
												items.map((item) => item.id)
											)
										}
										isLoading={isUserLoading}
										disabled={isAssignDisabled}
										placeholder="Seleziona utenti"
										classNameOverlay="max-h-[200px]"
									/>
								</div>

								{/* Date */}
								<div className="base">
									<div className="wrap_input_landing date">
										<label htmlFor="crud-task-due-date" className="label_input">
											Data di scadenza
										</label>
										<div className="relative">
											<input
												{...register("due_date")}
												className={`landing${errors.due_date ? " err" : ""}`}
												id="crud-task-due-date"
												type="date"
											/>
											<span className="icon_24 default calendar icon_mask !bg-interactive-100"></span>
										</div>
									</div>
								</div>
							</form>
						</div>

						<div className="main_footer_modal">
							<div className="right_content">
								{/* Main Cta */}
								<button
									type="submit"
									form="crud-task-form"
									className={`button fill success${loading ? " loading" : ""}`}
								>
									<div className="load_content">Conferma</div>
									<div className="main_loader_icon fade_in"></div>
								</button>
							</div>
						</div>
					</>
				) : (
					<div className="p-[40px] flex flex-col items-center text-center fade_in">
						<div className="icon_75 default success_check mb-[20px]"></div>
						<div className="text-[18px] font-bold text-balance">
							Creazione attività avvenuta con successo
						</div>

						<a
							href={`${process.env.REACT_APP_HOST_CLIENT_TODO}/tasks/${task?.id}`}
							target={"_blank"}
							className="button group fill secondary mt-[30px] !gap-[8px]"
							rel="noopener"
						>
							Apri attività
							<span className="icon_24 default link_out icon_mask !bg-primary-100 group-active:opacity-70"></span>
						</a>
					</div>
				)}
			</div>
		</ModalContainer>
	);
}
